import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { RankCell, GeoNameCell, PoiGrowthHeader } from '../components/home/table-cells';
import { renderCellValue } from './utils';
import { Body, BodyType, BodyColor, Heading, TypographyType, TypographyColor } from '../ui-elements';

interface YearOption {
    label: string;
    value: number;
}

interface ColumnHeader {
    accessorKey: string;
    header: string;
    enableSorting?: boolean;
    sortKey?: string;  // Field to use for sorting (e.g., "actualPopulation" for "population" column)
    children?: YearOption[];
}

interface BuildColumnsOptions {
    showPresence?: boolean;
    selectedYear?: number;
    onYearChange?: (year: number) => void;
    onGeoNameClick?: (row: any) => void;
    onRequestPoI?: (row: any) => void;
    sorting?: any[];
    disableSorting?: boolean;
    disableGrowthDropdown?: boolean;
}

// Generic sorting function that uses sortKey from header config
// Handles both string and number values
const createSortingFn = (sortKey: string) => (rowA: any, rowB: any) => {
    const a = rowA.original?.[sortKey];
    const b = rowB.original?.[sortKey];

    // Handle null/undefined - push to end
    if (a === null || a === undefined) return 1;
    if (b === null || b === undefined) return -1;

    // String comparison
    if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b);
    }

    // Number comparison
    return a - b;
};

/**
 * Builds column definitions for the Recommended Regions VirtualizedTable
 * Handles custom cell rendering for rankData, geoName, poi, and growth columns
 */
export const buildRecommendedColumns = (
    tableHeaderData: ColumnHeader[],
    options: BuildColumnsOptions = {}
): ColumnDef<any>[] => {
    const {
        showPresence = false,
        selectedYear = 5,
        onYearChange,
        onGeoNameClick,
        onRequestPoI,
        disableSorting = false,
        disableGrowthDropdown = false,
    } = options;

    if (!tableHeaderData || !Array.isArray(tableHeaderData)) {
        return [];
    }

    return tableHeaderData.map((header) => {
        const accessorKey = header.accessorKey;

        const baseColumn: any = {
            accessorKey,
            header: header.header,
            id: accessorKey,
            enableSorting: disableSorting ? false : header.enableSorting,
        };

        // Add sorting function if sortKey is provided
        if (header.sortKey) {
            baseColumn.sortingFn = createSortingFn(header.sortKey);
        }

        switch (accessorKey) {
            // Rank Column - shows rank badge with color and normalized score
            case 'rankData':
                return {
                    ...baseColumn,
                    cell: (info: any) => <RankCell data={info.getValue()} />,
                    sortingFn: (rowA: any, rowB: any) => {
                        const a = rowA.original?.rankData?.rank;
                        const b = rowB.original?.rankData?.rank;

                        // Get current sort direction for this column
                        const currentSort = options.sorting?.find((s: any) => s.id === 'rankData');
                        const isDesc = currentSort?.desc ?? false;

                        // Handle null values - always push to end regardless of sort direction
                        const aIsNull = a === null || a === undefined;
                        const bIsNull = b === null || b === undefined;

                        if (aIsNull && bIsNull) return 0;

                        // Adjust comparison based on sort direction to keep nulls at end
                        if (isDesc) {
                            if (aIsNull) return -1;  // After table inverts: +1, keeps null at end
                            if (bIsNull) return 1;   // After table inverts: -1, keeps null at end
                        } else {
                            if (aIsNull) return 1;   // Null at end for ascending
                            if (bIsNull) return -1;  // Null at end for ascending
                        }

                        return a - b;
                    }
                };

            // GeoName Column - clickable name with MBP presence chips
            case 'geoName':
                return {
                    ...baseColumn,
                    cell: (info: any) => {
                        const row = info.row.original;
                        return (
                            <GeoNameCell
                                geoName={info.getValue()}
                                presenceTypes={row.presence_types}
                                isFilteredOut={row.rankData?.filteredOut}
                                showPresence={showPresence && row.myPresence !== "null" && row.presence_types}
                                onClick={() => onGeoNameClick?.(row)}
                            />
                        );
                    }
                };

            // PoI Column - shows count or "Request PoI" link
            case 'poi':
                return {
                    ...baseColumn,
                    sortingFn: (rowA: any, rowB: any) => {
                        const sortKey = header.sortKey || 'poi';
                        const a = rowA.original?.[sortKey];
                        const b = rowB.original?.[sortKey];

                        // Get current sort direction for this column
                        const currentSort = options.sorting?.find((s: any) => s.id === 'poi');
                        const isDesc = currentSort?.desc ?? false;

                        // Handle null values - always push to end regardless of sort direction
                        const aIsNull = a === null || a === undefined;
                        const bIsNull = b === null || b === undefined;

                        if (aIsNull && bIsNull) return 0;

                        // Adjust comparison based on sort direction to keep nulls at end
                        if (isDesc) {
                            if (aIsNull) return -1;  // After table inverts: +1, keeps null at end
                            if (bIsNull) return 1;   // After table inverts: -1, keeps null at end
                        } else {
                            if (aIsNull) return 1;   // Null at end for ascending
                            if (bIsNull) return -1;  // Null at end for ascending
                        }

                        return a - b;
                    },
                    cell: (info: any) => {
                        const value = info.getValue();
                        const row = info.row.original;

                        if (value === null && onRequestPoI) {
                            return (
                                <span onClick={(e) => { e.stopPropagation(); onRequestPoI(row); }}>
                                    <Heading
                                        title="Request PoI"
                                        type={TypographyType.h5}
                                        colour={TypographyColor.primary}
                                        classname="p-0 text-underline cursor-pointer"
                                    />
                                </span>
                            );
                        }
                        return renderCellValue(info.getValue(), accessorKey, row);
                    }
                };

            // PoI Growth Column - header with year dropdown
            case 'growth': {
                const growthOptions = header.children || [];
                const selectedOption = growthOptions.find((opt: YearOption) => opt.value === selectedYear);
                const displayLabel = selectedOption
                    ? `PoI growth (${selectedOption.label})`
                    : `PoI growth (${selectedYear} ${selectedYear > 1 ? 'years' : 'year'})`;
                return {
                    ...baseColumn,
                    header: disableGrowthDropdown ? displayLabel : () => (
                        <PoiGrowthHeader
                            selectedYear={selectedYear}
                            options={growthOptions}
                            onYearChange={onYearChange}
                        />
                    ),
                    cell: (info: any) => {
                        const row = info.row.original;
                        return (
                            <Heading
                                title={info.getValue() ?? 'NA'}
                                type={TypographyType.h5}
                                colour={TypographyColor.primary}
                                classname="p-0 m-0"
                            />
                        );
                    }
                };
            }

            // Default columns (area, population, eh, ctv, etc.)
            default:
                return {
                    ...baseColumn,
                    cell: (info: any) => {
                        const row = info.row.original;
                        return renderCellValue(info.getValue(), accessorKey, row);
                    }
                };
        }
    });
};
